import { useEffect, useState } from "react";
import { Button, Group, Text, TextInput, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import ApiSession from "../../api";
import EmailListTable from "../../components/common/EmailListTable";
import SEConfirmModal from "../../components/common/SEConfirmModal";
import { getErrorMessage } from "../../utils/errorUtils";
import { isValidEmail } from "../../constants/helper";

const apiSession = new ApiSession();
const MAX_EMAILS = 5;

const TicketNotification = () => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [listError, setListError] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);

  const fetchEmails = () => {
    apiSession
      .getTicketNotification()
      .then((res) => {
        setEmails(Array.isArray(res) ? res : []);
      })
      .catch((err) => {
        setEmails([]);
        setListError(getErrorMessage(err));
      });
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setEmailInput("");
    setEmailError("");
    setEditingEmail(null);
  };

  const handleSave = () => {
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) {
      setEmailError(t("common.required") || "Email is required");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setEmailError(t("common.invalidEmail") || "Enter a valid email address");
      return;
    }
    if (!editingEmail && emails?.length >= MAX_EMAILS) {
      setEmailError(t("ticketNotification.maxEmailsReached"));
      return;
    }
    if (editingEmail && trimmedEmail === editingEmail) {
      closeModal();
      return;
    }
    setSubmitting(true);
    const request = editingEmail
      ? apiSession
          .deleteTicketNotification(editingEmail)
          .then(() => apiSession.saveTicketNotification({ email: trimmedEmail }))
      : apiSession.saveTicketNotification({ email: trimmedEmail });
    request
      .then(() => {
        setSubmitting(false);
        closeModal();
        fetchEmails();
      })
      .catch((err) => {
        setSubmitting(false);
        setEmailError(getErrorMessage(err));
      });
  };

  const handleEdit = (email) => {
    setEditingEmail(email);
    setEmailInput(email);
    setEmailError("");
    setModalOpen(true);
  };

  const handleDelete = (email) => {
    setEmailToDelete(email);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setEmailToDelete(null);
  };

  const confirmDelete = () => {
    setDeleting(true);
    apiSession
      .deleteTicketNotification(emailToDelete)
      .then(() => {
        setDeleting(false);
        closeDeleteModal();
        fetchEmails();
      })
      .catch((err) => {
        setDeleting(false);
        setListError(getErrorMessage(err));
        closeDeleteModal();
      });
  };

  return (
    <>
      <Title order={2}>{t("ticketNotification.title")}</Title>
      <Text mb="md">{t("ticketNotification.description")}</Text>
      {listError && <p style={{ color: "red" }}>{listError}</p>}
      <div style={{ width: "30%", minWidth: 320, margin: 0 }}>
        <EmailListTable data={emails} onDelete={handleDelete} onEdit={handleEdit} />
        <Group justify="flex-start" mt="md" className="mb-3">
          <Button
            aria-label="ticket-add-email"
            variant="outline"
            disabled={emails.length >= MAX_EMAILS}
            onClick={() => setModalOpen(true)}
          >
            {t("ticketNotification.addEmail")}
          </Button>
        </Group>
        {emails.length >= MAX_EMAILS && (
          <Text align="left" size="sm" color="dimmed">
            {t("ticketNotification.maxEmailsReached")}
          </Text>
        )}
      </div>

      {modalOpen && (
        <SEConfirmModal
          opened={modalOpen}
          onClose={closeModal}
          onConfirm={handleSave}
          header={t(
            editingEmail
              ? "ticketNotification.editEmailModal.header"
              : "ticketNotification.addEmailModal.header"
          )}
          confirmLabel={t("common.save")}
          cancelLabel={t("common.cancel")}
          confirmDisabled={!emailInput || submitting}
          confirmLoading={submitting}
          confirmLoadingLabel={t("sideMenu.versionDetails.saving")}
          message={
            <TextInput
              label={t("ticketNotification.addEmailModal.emailLabel")}
              placeholder={t("ticketNotification.addEmailModal.emailPlaceholder")}
              value={emailInput}
              onChange={(e) => {
                setEmailInput(e.currentTarget.value);
                setEmailError("");
              }}
              required
              error={emailError}
            />
          }
        />
      )}

      {deleteModalOpen && (
        <SEConfirmModal
          opened={deleteModalOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          header={t("common.alert")}
          message={t("ticketNotification.deleteConfirm", { email: emailToDelete })}
          confirmLabel={t("common.yes")}
          cancelLabel={t("common.no")}
          confirmLoading={deleting}
          confirmLoadingLabel={t("userManagement.userTable.deleting")}
        />
      )}
    </>
  );
};

export default TicketNotification;

